<template>
  <div class="flex flex-wrap items-center justify-end gap-1 issue-home-header-actions">
    <v-button-icon-round
      v-if="subactivityId"
      icon="arrow-left"
      color="grey-darkest"
      tooltip="back_to_subactivity"
      tooltip-position="bottom"
      class="mr-1"
      @click="$router.push({ name: 'subactivity.home', params: { ubactivity_id: subactivityId, id: activityId } })"
    />
    <v-button-icon-round
      v-else
      icon="arrow-left"
      color="grey-darkest"
      tooltip="back_to_main_activity"
      tooltip-position="bottom"
      class="mr-1"
      @click="$router.push({ name: 'activity.home', params: { id: activityId } })"
    />

    <add-validation-flow
      v-if="canAddValidationFlow"
      :entity-id="entityId"
      entity-type="issue"
      open-modal-with="button-icon"
    />

    <div class="relative">
      <v-button-icon-round
        v-if="canChangeStatus"
        icon="undo-alt"
        tooltip="change_status"
        tooltip-position="bottom"
        class="mr-1"
        :color="statusColor"
        @click="handleClick"
      />

      <div
        v-if="isOpen"
        class="absolute top-12 right-0 z-50 w-72 bg-white border border-gray-300 rounded-lg shadow-xl"
      >
        <div class="px-4 py-2 bg-gray-100 border-b border-gray-200 rounded-t-lg">
          <h3 class="text-sm font-semibold text-gray-700">{{ $t('date') }}</h3>
        </div>
        <div class="p-4">
          <v-input
            id="issue-close-date"
            type="date"
            v-model="selectedCloseDate"
            class="mt-2 w-full p-2 border border-gray-300 rounded-md shadow-sm sm:text-sm"
          />
        </div>
        <div class="flex justify-end gap-2 px-4 py-3 bg-gray-50 border-t border-gray-200 rounded-b-lg">
          <v-button
            class="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-100"
            @click="cancel"
          >
            {{ $t('cancel') }}
          </v-button>
          <v-button @click="submitDate">
            {{ $t('save') }}
          </v-button>
        </div>
      </div>
    </div>

    <v-button-icon-round
      v-if="canGenerateReport"
      icon="file-alt"
      tooltip="generate_report"
      tooltip-position="bottom"
      class="mr-1"
      @click="generateReport"
    />

    <v-button-icon-round
      v-if="canEdit"
      icon="pencil-alt"
      tooltip="edit"
      tooltip-position="bottom"
      class="mr-1"
      @click="onClickEdit"
    />

    <v-button-icon-round
      v-if="canDelete"
      color="red"
      icon="trash-alt"
      tooltip="remove"
      tooltip-position="bottom"
      @click="onClickDelete"
    />

    <v-button-icon-round
      icon="arrow-right"
      color="grey-darkest"
      tooltip="issue_info"
      tooltip-position="left"
      class="ml-2"
      @click="showSide"
    />
  </div>
</template>

<script>
import { mapGetters } from 'vuex'
import axios from 'axios'
import EventBus from '~/plugins/bus'
import addValidationFlow from '·/components/add-validation-flow'

export default {
  name: 'IssueHomeHeaderActions',

  components: {
    addValidationFlow,
  },

  props: {
    entityId: { type: Number, required: true, },
    activityId: { type: [Number, String], default: null, },
    subactivityId: { type: [Number, String], default: null, },
    issue: { type: Object, required: true, },
    issueTasks: { type: Array, default: () => [], },
    statusColor: { type: String, default: 'blue', },
  },

  data: () => ({
    isOpen: false,
    selectedCloseDate: '',
  }),

  computed: {
    ...mapGetters({
      project: 'app/project',
      company: 'app/company',
    }),

    isOpenStatus() {
      return !!this.issue.is_open
    },

    // Project collaborators must not get header actions, even if they created the issue
    isProjectCollaborator() {
      return !!this.project.can_collaborate
    },

    canAddValidationFlow() {
      if (this.project.issue_template == 1) {
        return this.project.can_issue_admin
      }

      if (this.isProjectCollaborator) {
        return false
      }

      return this.project.can_admin
        || this.issue.is_creator
        || this.issue.is_manager
        || this.issue.is_project_manager
    },

    canChangeStatus() {
      if (this.project.issue_template == 1) {
        return this.project.can_issue_admin
      }

      if (this.isProjectCollaborator) {
        return false
      }

      return this.issue.is_manager || this.project.can_issue || this.issue.is_project_manager
    },

    canGenerateReport() {
      if (this.project.issue_template == 1) {
        return this.project.can_issue_admin
      }

      if (this.isProjectCollaborator) {
        return false
      }

      return this.project.can_admin
        || this.project.can_issue
        || this.issue.is_manager
        || this.issue.is_creator
    },

    canEdit() {
      if (this.project.issue_template == 1) {
        return this.project.can_issue_admin
      }

      if (this.isProjectCollaborator) {
        return false
      }

      return this.project.can_issue
        || this.issue.is_creator
        || this.issue.is_manager
        || this.issue.is_project_manager
    },

    canDelete() {
      if (this.project.issue_template == 1) {
        return this.project.can_issue_admin
      }

      if (this.isProjectCollaborator) {
        return false
      }

      return this.project.can_admin || this.issue.is_creator || this.issue.is_project_manager
    },
  },

  methods: {
    handleClick() {
      if (this.issue.assignable_type === 'App\\Models\\AssignmentLine' && this.issue.is_open) {
        EventBus.$emit('issue-home:register-task', {
          assignment: this.issue.assignable.assignment,
          isReal: false,
          actionType: 4,
        })
        return
      }

      if (this.isOpenStatus && this.project.issue_template == 1) {
        const pendingTasks = this.issueTasks.filter(task => task.status != 1)
        if (pendingTasks.length) {
          this.$buefy.toast.open({
            message: this.$t('issue_cant_close_with_pending_tasks'),
            type: 'is-danger',
            position: 'is-top-right',
            duration: 5000,
          })
        } else {
          this.isOpen = !this.isOpen
        }
      } else {
        this.submitDate()
      }
    },

    cancel() {
      this.isOpen = false
    },

    submitDate() {
      this.changeStatus()
      this.isOpen = false
    },

    async changeStatus() {
      const newStatus = this.issue.is_open ? 0 : 1

      try {
        const { data } = await axios.post('/api/v2/issue/' + this.issue.id + '/status', {
          status: newStatus,
          close_date: this.selectedCloseDate,
        })

        if (data && data.success) {
          this.issue.is_open = newStatus
          EventBus.$emit('issue-home:status-changed', {
            isOpen: !!newStatus,
            statusColor: newStatus ? 'blue' : 'grey',
          })
          this.$notify.success('success_updating_status_issue')
        } else {
          this.$notify.error('error_updating_status_issue')
        }
      } catch (error) {
        console.log(error.response)
        this.$notify.error('error_updating_status_issue')
      }
    },

    onClickEdit() {
      this.$router.push({ name: 'issue.edit', params: { issue_id: this.issue.id } })
    },

    onClickDelete() {
      this.$dialog.confirm({
        title: this.$t('delete_issue'),
        message: this.$t('delete_issue_confirm_text', [this.issue.name]),
        confirmText: this.$t('delete_issue'),
        type: 'is-danger',
        hasIcon: true,
        onConfirm: async () => {
          const { data } = await axios.post('/api/v2/issue/' + this.issue.id + '/delete')
          if (data && data.success) {
            this.$notify.success('success_deleting_issue')
            this.$router.push({ name: 'activity.home', activity_id: this.activityId })
          } else {
            this.$notify.error(data.error)
          }
        },
      })
    },

    generateReport() {
      this.$dialog.confirm({
        title: this.$t('generate_report'),
        message: this.$t('confirm_generate_report', [this.issue.name]),
        confirmText: this.$t('generate_report'),
        type: 'is-success',
        hasIcon: true,
        onConfirm: async () => {
          const { data } = await axios.get('/api/v2/issue/' + this.issue.id + '/report/' + this.company.alias)
          if (data) {
            window.open(data.data)
          } else {
            this.$notify.error('error_download_report')
          }
        },
      })
    },

    showSide() {
      EventBus.$emit('issue-home:show-side')
    },
  },
}
</script>
